/* ============================================
   VISITREQUESTS.JSX
   📁 src/pages/sellers/VisitRequests.jsx
   ============================================ */

import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, CheckCircle, XCircle, Filter
} from 'lucide-react';
import '../../../assets/css/sellers/dashboard/sellervisitrequest/visitrequests.css';
import SellerMasterLayout from 'layouts/SellerMasterLayout';
import axios from 'axios';
import AppContext from 'context/AppContext';

function VisitRequests() {
  const { baseUrl, baseUrlForFiles, loggedInSeller, authToken } = useContext(AppContext);
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState('All');
  const [tableLoading, setTableLoading] = useState(true);
  const [visitRequests, setVisitRequests] = useState([]);

  // ── Fetch ────────────────────────────────────────────────────
  const viewAllVisitRequests = () => {
    setTableLoading(true);

    axios
      .post(`${baseUrl}/seller/visitRequests/viewAllVisitRequests`, {}, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      }
      )
      .then((response) => {
        console.log('Visit Requests:', response.data);
        setVisitRequests(response.data.visitRequests || []);
        setTableLoading(false);
      })
      .catch((error) => {  
        console.log(error.response);
        console.log(error.response?.data);
    setTableLoading(false);
  });
};

useEffect(() => { viewAllVisitRequests(); }, []);

// ── Stats counts ─────────────────────────────────────────────
const totalRequests = visitRequests.length;
const pendingRequests = visitRequests.filter(r => r.status === 'pending').length;
const acceptedRequests = visitRequests.filter(r => r.status === 'accepted').length;
const rejectedRequests = visitRequests.filter(r => r.status === 'rejected').length;

// ── Accept / Reject handlers ──────────────────────────────────
const handleAccept = (id) => {
  changeVisitRequestStatus(id, "accepted");
};

const handleReject = (id) => {
  changeVisitRequestStatus(id, "rejected");
};


const changeVisitRequestStatus = (id, status) => {
  axios
    .post(`${baseUrl}/seller/visitRequests/changeVisitRequestStatus`, {
      visit_request_id: id,
      status: status,
    },
      {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
    .then((response) => {
      if (response.data.success) {
        viewAllVisitRequests();
      } else {
        alert(response.data.message);
      }
    })
    .catch((error) => {
      console.log(error);
    });
};

// ── Filter ───────────────────────────────────────────────────
const filteredRequests =
  statusFilter === 'All'
    ? visitRequests
    : visitRequests.filter(r => r.status === statusFilter.toLowerCase());

// ── Status badge class ────────────────────────────────────────
const getStatusClass = (status) => {
  switch (status) {
    case 'pending': return 'vr-status-pending';
    case 'accepted': return 'vr-status-accepted';
    case 'rejected': return 'vr-status-rejected';
    default: return 'vr-status-pending';
  }
};

// ── RENDER ───────────────────────────────────────────────────
return (
  <SellerMasterLayout pageTitle="Visit Requests">
    <div className="vr-wrapper">

      {/* ── Stat Cards ──────────────────────────────────── */}
      <div className="vr-stats-row">

        <div className="vr-stat-card" style={{ animationDelay: '0.05s' }}>
          <div className="vr-stat-icon vr-icon-blue"><Calendar size={26} /></div>
          <div className="vr-stat-text">
            <h3 className="vr-stat-num">{totalRequests}</h3>
            <p className="vr-stat-lbl">Total Requests</p>
          </div>
        </div>

        <div className="vr-stat-card" style={{ animationDelay: '0.10s' }}>
          <div className="vr-stat-icon vr-icon-orange"><Clock size={26} /></div>
          <div className="vr-stat-text">
            <h3 className="vr-stat-num">{pendingRequests}</h3>
            <p className="vr-stat-lbl">Pending Requests</p>
          </div>
        </div>

        <div className="vr-stat-card" style={{ animationDelay: '0.15s' }}>
          <div className="vr-stat-icon vr-icon-green"><CheckCircle size={26} /></div>
          <div className="vr-stat-text">
            <h3 className="vr-stat-num">{acceptedRequests}</h3>
            <p className="vr-stat-lbl">Accepted Requests</p>
          </div>
        </div>

        <div className="vr-stat-card" style={{ animationDelay: '0.20s' }}>
          <div className="vr-stat-icon vr-icon-red"><XCircle size={26} /></div>
          <div className="vr-stat-text">
            <h3 className="vr-stat-num">{rejectedRequests}</h3>
            <p className="vr-stat-lbl">Rejected Requests</p>
          </div>
        </div>

      </div>


      {/* ── Table ───────────────────────────────────────── */}
      <div className="vr-table-container">
        <table className="vr-table">
          <thead>
            <tr>
              <th>Buyer</th>
              <th>Property</th>
              <th>Booking Date</th>
              <th>Booking Time</th>
              <th>Status</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan="6">
                  <div className="vr-loader">
                    <div className="vr-loader-spinner"></div>
                    <p>Loading Visit Request Records...</p>
                  </div>
                </td>
              </tr>
            ) : filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="6">
                  <div className="vr-empty">
                    <Calendar size={44} className="vr-empty-icon" />
                    <h3>No visit requests found</h3>
                    <p>There are no {statusFilter.toLowerCase()} requests at the moment.</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredRequests.map((req) => (
                <tr key={req.id} className="vr-row">

                  {/* Buyer */}
                  <td>
                    <div className="vr-user-cell">
                      <img
                        src={`${baseUrlForFiles}${req.exbuyer?.profileImage}`}
                        alt={req.exbuyer?.firstName}
                        className="vr-avatar"
                        onError={e => { e.target.src = 'https://via.placeholder.com/40?text=B'; }}
                      />
                      <div>
                        <p className="vr-user-name">
                          {req.exbuyer?.firstName} {req.exbuyer?.lastName}
                        </p>
                        <p className="vr-user-email">{req.exbuyer?.email}</p>
                      </div>
                    </div>
                  </td>

                  {/* Property */}
                  <td>
                    <div className="vr-prop-cell">
                      <img
                        src={`${baseUrlForFiles}${req.exproperty?.expropertyimages?.[0]?.images}`}
                        alt={req.exproperty?.property_title}
                        className="vr-prop-thumb"
                        onError={e => { e.target.src = 'https://via.placeholder.com/72x52?text=P'; }}
                      />
                      <span className="vr-prop-name">{req.exproperty?.property_title}</span>
                    </div>
                  </td>

                  {/* Date */}
                  <td>
                    <span className="vr-date-text">{req.bookingDate}</span>
                  </td>

                  {/* Time */}
                  <td>
                    <div className="vr-time-cell">
                      <Clock size={14} className="vr-time-icon" />
                      <span>{req.bookingTime}</span>
                    </div>
                  </td>

                  {/* Status */}
                  <td>
                    <span className={`vr-status-badge ${getStatusClass(req.status)}`}>
                      {req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                    </span>
                  </td>

                  {/* ✅ Actions — circle buttons with color change */}
                  <td>
                    <div className="vr-actions">

                      {/* Accept button */}
                      <button
                        className={`vr-action-btn vr-btn-accept ${req.status === 'accepted' ? 'vr-btn-accept--done' : ''}`}
                        onClick={() => req.status === 'pending' && handleAccept(req.id)}
                        title="Mark as Accepted"
                        disabled={req.status !== 'pending'}
                      >
                        <CheckCircle size={22} strokeWidth={2} />
                      </button>

                      {/* Reject button */}
                      <button
                        className={`vr-action-btn vr-btn-reject ${req.status === 'rejected' ? 'vr-btn-reject--done' : ''}`}
                        onClick={() => req.status === 'pending' && handleReject(req.id)}
                        title="Mark as Rejected"
                        disabled={req.status !== 'pending'}
                      >
                        <XCircle size={22} strokeWidth={2} />
                      </button>

                    </div>
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  </SellerMasterLayout>
);
}

export default VisitRequests;